import { isApiRequestError } from '@/request/error';
import { isRequestCancellation } from '@/request/transport-error';
import { submitDiagnostic, type DiagnosticSource } from './diagnostics';

export function isNativeError(error: unknown): error is Error {
  try {
    return (
      error instanceof Error ||
      (typeof error === 'object' &&
        error !== null &&
        !(Symbol.toStringTag in error) &&
        Object.prototype.toString.call(error) === '[object Error]')
    );
  } catch {
    return false;
  }
}

export function normalizeThrown(error: unknown): Error {
  if (isNativeError(error)) {
    // SDK 会展开 Error 的可枚举属性，不能把请求配置等附加字段原样交给它。
    const { message, name, stack } = error;
    const safeError = new Error(typeof message === 'string' ? message : 'Non-string Error message');
    safeError.name = typeof name === 'string' ? name : 'Error';
    safeError.stack = typeof stack === 'string' ? stack : undefined;
    return safeError;
  }
  if (typeof error === 'string') return new Error(error);
  if (typeof error === 'number' || typeof error === 'boolean' || typeof error === 'bigint') {
    return new Error(String(error));
  }
  return new Error('Non-Error value thrown');
}

export function reportException(error: unknown, source?: DiagnosticSource): void {
  try {
    if (isApiRequestError(error) || isRequestCancellation(error)) return;
    const safeError = normalizeThrown(error);
    if (source) safeError.message = `[${source}] ${safeError.message}`;
    submitDiagnostic({ kind: 'exception', error: safeError });
  } catch {
    // 异常字段也可能是会抛错的 getter，使用固定记录保护最终处理边界。
    submitDiagnostic({ kind: 'exception', error: new Error('Failed to read thrown value') });
  }
}
